import React, { useState, useEffect, useMemo } from 'react';
import FullBlockingModal from '../common/FullBlockingModal';
import { Job } from '../../types/job';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import JobPreview from './JobPreview';
import PrioritySelector from './PrioritySelector';
import { useListingTypeSelector } from '../../hooks/useListingTypeSelector';
import WalletSummary from '../credits/WalletSummary';
import { useCreditStore } from '../../stores/creditStore';
import { toTorontoDateValue, torontoEndOfDayIso } from './jobCreation/jobCreationDates';

interface PublishJobModalProps {
  job: Job;
  onClose: () => void;
  onEdit: (jobId: string) => void;
  onPublish: (
    listingTypeId: number,
    expiresAt: string,
    selectionDate: string | undefined,
    publicationRequestId: string
  ) => void | Promise<void>;
}

const TWO_WEEKS_DAYS = 14;

const padTwo = (value: number) => String(value).padStart(2, '0');

/**
 * Adds `days` to a `YYYY-MM-DD` date-only value using pure calendar-day
 * arithmetic (never converted to/from a real timezone instant), so it can't
 * be off by a day near a DST boundary. This is unrelated to "what instant
 * does this date-only value correspond to in Toronto" — see
 * `torontoEndOfDayIso` for that.
 */
const addCalendarDays = (value: string, days: number): string => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${padTwo(date.getUTCMonth() + 1)}-${padTwo(date.getUTCDate())}`;
};

const PublishJobModal: React.FC<PublishJobModalProps> = ({ job, onClose, onEdit, onPublish }) => {
  const { t } = useTranslation(['jobs', 'common']);
  const { options, selectedOption, setSelectedOption } = useListingTypeSelector();
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const { balance, refreshBalance } = useCreditStore();

  const minExpiresAt = useMemo(() => toTorontoDateValue(new Date()), []);
  // A start_date that has already elapsed (e.g. a shadow job published long
  // after it was drafted) doesn't constrain expires_at — the "expiration
  // after start" check only guards against newly-created misconfigurations.
  const upcomingStartDate =
    job.start_date && job.start_date >= minExpiresAt ? job.start_date : null;
  const defaultExpiresAt = useMemo(() => {
    const twoWeeksOut = addCalendarDays(minExpiresAt, TWO_WEEKS_DAYS);
    return upcomingStartDate && upcomingStartDate < twoWeeksOut ? upcomingStartDate : twoWeeksOut;
  }, [minExpiresAt, upcomingStartDate]);
  const [expiresAt, setExpiresAt] = useState(defaultExpiresAt);
  const [selectionDate, setSelectionDate] = useState('');
  // Stable for the lifetime of this modal instance (i.e. one "publish attempt"),
  // so a retried/double-fired publish click dedupes server-side instead of
  // generating a fresh id (and bypassing the dedupe) each time.
  const [publicationRequestId] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);

  const hasEnoughCredits = balance && selectedOption && selectedOption.cost <= balance;
  // Publication deadlines are Toronto calendar days, so "in the past" must be
  // decided against Toronto's current calendar date, not the browser's local
  // date or a UTC-anchored `Date` comparison (both can be off by hours around
  // the Toronto evening and DST boundaries).
  const isExpiresAtInPast = Boolean(expiresAt) && expiresAt < toTorontoDateValue(new Date());
  const isAfterStartDate = Boolean(upcomingStartDate) && expiresAt > upcomingStartDate!;
  const isExpiresAtValid = Boolean(expiresAt) && !isExpiresAtInPast && !isAfterStartDate;

  const minSelectionDate = useMemo(
    () => (expiresAt ? addCalendarDays(expiresAt, 1) : ''),
    [expiresAt]
  );
  const isSelectionDateBeforeExpiresAt = Boolean(selectionDate) && selectionDate <= expiresAt;
  const isSelectionDateAfterStartDate =
    Boolean(selectionDate) && Boolean(upcomingStartDate) && selectionDate > upcomingStartDate!;
  const isSelectionDateValid =
    !selectionDate || (!isSelectionDateBeforeExpiresAt && !isSelectionDateAfterStartDate);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[72rem]">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary mb-6">
          {t('publish_job_title')}
        </h2>

        <JobPreview
          job={{
            title: job.title,
            requiredServices: job.required_services,
            city: job.city,
            onSite: job.on_site,
            description: job.description,
            languages: job.languages,
            budgetRange: job.budget_range,
            startDate: job.start_date!,
            endDate: job.end_date!,
          }}
        />
        <div className="border-t pt-6 mt-4">
          <div className="text-xl font-bold text-secondary mb-4">{t('publish.priority_title')}</div>

          <PrioritySelector
            priorities={options}
            selectedPriority={selectedOption}
            setSelectedPriority={setSelectedOption}
            error={errors.priority}
            clearError={() => setErrors(prev => ({ ...prev, priority: undefined }))}
          />

          <div>
            <label htmlFor="job-expires-at" className="text-xl font-bold text-secondary mb-2 block">
              {t('publish.expires_at_title')}
            </label>
            <p className="text-sm text-secondary mb-3">{t('publish.expires_at_help')}</p>
            <input
              id="job-expires-at"
              type="date"
              min={minExpiresAt}
              max={upcomingStartDate ?? undefined}
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              className="w-full md:w-72 rounded-lg border-2 border-bg-secondary-light px-3 py-2 text-secondary focus:border-primary focus:outline-none"
            />
            {isExpiresAtInPast && (
              <p className="text-sm text-red-600 mt-2">
                {t('publish.expires_at_before_today_error')}
              </p>
            )}
            {!isExpiresAtInPast && isAfterStartDate && (
              <p className="text-sm text-red-600 mt-2">
                {t('publish.expires_at_after_start_date_error', { startDate: job.start_date })}
              </p>
            )}
          </div>

          <div className="mt-6">
            <label
              htmlFor="job-selection-date"
              className="text-xl font-bold text-secondary mb-2 block"
            >
              {t('publish.selection_date_title')}
            </label>
            <p className="text-sm text-secondary mb-3">{t('publish.selection_date_help')}</p>
            <input
              id="job-selection-date"
              type="date"
              min={minSelectionDate}
              max={upcomingStartDate ?? undefined}
              value={selectionDate}
              onChange={e => setSelectionDate(e.target.value)}
              className="w-full md:w-72 rounded-lg border-2 border-bg-secondary-light px-3 py-2 text-secondary focus:border-primary focus:outline-none"
            />
            {isSelectionDateBeforeExpiresAt && (
              <p className="text-sm text-red-600 mt-2">
                {t('publish.selection_date_before_expires_at_error')}
              </p>
            )}
            {!isSelectionDateBeforeExpiresAt && isSelectionDateAfterStartDate && (
              <p className="text-sm text-red-600 mt-2">
                {t('publish.selection_date_after_start_date_error', { startDate: job.start_date })}
              </p>
            )}
          </div>

          <div className="flex flex-col mt-6 gap-6 md:flex-row-reverse md:flex-wrap md:justify-between md:items-center">
            <div className="flex flex-col md:flex-row-reverse gap-4 mt-6">
              <Button
                onClick={async () => {
                  if (submitting || !(selectedOption && isExpiresAtValid && isSelectionDateValid)) {
                    return;
                  }
                  setSubmitting(true);
                  try {
                    await onPublish(
                      selectedOption.id,
                      torontoEndOfDayIso(expiresAt),
                      selectionDate ? torontoEndOfDayIso(selectionDate) : undefined,
                      publicationRequestId
                    );
                  } finally {
                    setSubmitting(false);
                  }
                }}
                type="button"
                disabled={
                  submitting ||
                  !selectedOption ||
                  !hasEnoughCredits ||
                  !isExpiresAtValid ||
                  !isSelectionDateValid
                }
                variant="primary"
                className="w-full md:w-auto md:px-8"
              >
                {submitting
                  ? t('publish.loading')
                  : selectedOption
                    ? t('publish.use_credits_and_publish', { count: selectedOption.cost })
                    : t('publish.use_credits_and_publish', { count: 1 })}
              </Button>

              <Button
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
                onClick={() => {
                  onEdit(job.id);
                }}
                type="button"
                disabled={submitting}
              >
                {t('publish.edit')}
              </Button>

              <Button
                className="w-full md:w-auto md:px-8"
                variant="simple-text"
                onClick={onClose}
                type="button"
                disabled={submitting}
              >
                {t('publish.cancel')}
              </Button>
            </div>

            <WalletSummary
              outerClassName="w-full md:w-full md:max-w-[28rem]"
              modalParams={{
                type: 'publishJob',
                jobId: job.id,
              }}
            />
          </div>
        </div>
      </FullBlockingModal>
    </>
  );
};

export default PublishJobModal;
